/**
 * Fog of war on the World Map (2026-10-30). Pure: a policy and the hero's own
 * state go in, the places the hero has charted and the places they have only
 * heard of come out. Which policy applies, and why (an event, a season, a
 * test), is worldMapFogPolicy.ts's job; with no policy the map has no fog.
 *
 * Like known places, fog hides presentation only: the portal graph, places and
 * roads are the same whether or not a hero has charted them.
 */
import { getPocketParent } from '../../../../shared/logic/pocketMaps';
import type { WorldMapModel } from './worldMapModel';

/**
 * What a policy reveals:
 * - `visited`: every place this character has entered (server-owned `knownPlaces`);
 *   a known pocket charts its parent.
 * - `current`: the place the hero stands in now.
 * - `roads`: places one road from a charted place show as rumoured (marked, unnamed).
 */
export type WorldMapFogReveal = 'visited' | 'current' | 'roads';

export interface WorldMapFogPolicy {
  readonly id: string;
  readonly reveal: readonly WorldMapFogReveal[];
}

export interface WorldMapFogState {
  readonly currentMapId?: string | null;
  readonly knownPlaceIds?: readonly string[] | null;
}

export interface WorldMapFog {
  readonly policyId: string;
  readonly charted: ReadonlySet<string>;
  readonly rumoured: ReadonlySet<string>;
}

/** The fog for this hero under `policy`, or null when there is no fog. */
export function chartWorldMap(
  model: Pick<WorldMapModel, 'places' | 'roads'>,
  policy: WorldMapFogPolicy | null,
  state: WorldMapFogState,
): WorldMapFog | null {
  if (!policy) return null;
  const places = new Set(model.places.map(place => place.id));
  const charted = new Set<string>();
  const chart = (id: string | null | undefined) => {
    const placeId = id && (places.has(id) ? id : getPocketParent(id));
    if (placeId && places.has(placeId)) charted.add(placeId);
  };
  if (policy.reveal.includes('visited')) for (const id of state.knownPlaceIds ?? []) chart(id);
  if (policy.reveal.includes('current')) chart(state.currentMapId);
  const rumoured = new Set<string>();
  if (policy.reveal.includes('roads')) {
    for (const road of model.roads) {
      if (charted.has(road.from) && !charted.has(road.to)) rumoured.add(road.to);
      if (charted.has(road.to) && !charted.has(road.from)) rumoured.add(road.from);
    }
  }
  return { policyId: policy.id, charted, rumoured };
}

/** How a place shows under the fog: fully, as an unnamed rumour, or not at all. */
export function placeVisibility(fog: WorldMapFog | null, placeId: string): 'charted' | 'rumoured' | 'hidden' {
  if (!fog || fog.charted.has(placeId)) return 'charted';
  return fog.rumoured.has(placeId) ? 'rumoured' : 'hidden';
}
